// ABOUTME: Portal-based tooltip anchored above (or below) its trigger, with ARIA semantics,
// ABOUTME: keyboard/focus support, empty-content guard, viewport clamping (H+V), and scroll/resize dismissal.
import React, {useCallback, useEffect, useId, useLayoutEffect, useRef, useState} from 'react'
import ReactDOM from 'react-dom'

interface CountryConfigsTooltipProps {
  content: string
  delayMs?: number
  children: React.ReactNode
  tooltipClassName?: string
}

const VIEWPORT_MARGIN = 8
const GAP = 4

interface Anchor {
  cx: number
  triggerTop: number
  triggerBottom: number
}

interface Position {
  left: number
  top: number
  above: boolean
}

export const CountryConfigsTooltip: React.FC<CountryConfigsTooltipProps> = ({
  content,
  delayMs = 500,
  children,
  tooltipClassName,
}) => {
  const isEmpty = content.trim() === ''
  const [anchor, setAnchor] = useState<Anchor | null>(null)
  const [position, setPosition] = useState<Position | null>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<number | null>(null)
  const tooltipId = useId()

  const isVisible = anchor !== null && position !== null

  const hide = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setAnchor(null)
    setPosition(null)
  }, [])

  const show = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    setPosition(null)
    setAnchor({
      cx: rect.left + rect.width / 2,
      triggerTop: rect.top,
      triggerBottom: rect.bottom,
    })
  }, [])

  // After the tooltip renders, measure its real width/height and compute clamped position
  useLayoutEffect(() => {
    if (!anchor || !tooltipRef.current) return
    const width = tooltipRef.current.offsetWidth
    const height = tooltipRef.current.offsetHeight

    const left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(anchor.cx - width / 2, window.innerWidth - width - VIEWPORT_MARGIN),
    )

    const canFitAbove = anchor.triggerTop - GAP - height >= VIEWPORT_MARGIN
    const belowTop = Math.min(
      anchor.triggerBottom + GAP,
      window.innerHeight - height - VIEWPORT_MARGIN,
    )
    const top = canFitAbove ? anchor.triggerTop - GAP : Math.max(VIEWPORT_MARGIN, belowTop)

    setPosition({left, top, above: canFitAbove})
  }, [anchor])

  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    if (delayMs > 0) {
      timeoutRef.current = window.setTimeout(show, delayMs)
    } else {
      show()
    }
  }, [delayMs, show])

  const handleMouseLeave = useCallback(() => {
    hide()
  }, [hide])

  const handleFocus = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    show()
  }, [show])

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        hide()
      }
    },
    [hide],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') hide()
    },
    [hide],
  )

  // Dismiss tooltip on scroll or resize while shown
  useEffect(() => {
    if (!anchor) return
    window.addEventListener('scroll', hide, true)
    window.addEventListener('resize', hide)
    return () => {
      window.removeEventListener('scroll', hide, true)
      window.removeEventListener('resize', hide)
    }
  }, [anchor, hide])

  // Clean up pending timer on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  if (isEmpty) {
    return (
      <div ref={triggerRef} className="relative">
        {children}
      </div>
    )
  }

  return (
    <div
      ref={triggerRef}
      className="relative"
      tabIndex={-1}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      aria-describedby={isVisible ? tooltipId : undefined}
    >
      {children}
      {anchor &&
        ReactDOM.createPortal(
          <div
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            className="fixed z-[9999] pointer-events-none"
            style={{
              left: `${position?.left ?? anchor.cx}px`,
              top: `${position?.top ?? anchor.triggerTop}px`,
              transform: !position
                ? 'translate(-50%, -100%)'
                : position.above
                ? 'translateY(-100%)'
                : 'translateY(0)',
              visibility: position ? 'visible' : 'hidden',
            }}
          >
            <div className={`px-3 py-2 text-sm rounded-md shadow-lg max-w-[min(20rem,calc(100vw-16px))] whitespace-normal break-words bg-container_background_contrast text-foreground_inverted${tooltipClassName ? ` ${tooltipClassName}` : ''}`}>
              {content}
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}
